/**
 * Generated by orval 🍺
 * Do not edit manually.
 * designs-backend
 * The event stream: a WebSocket each signed-in page holds open, over which
 * every change clients should hear about is pushed as it happens, by anyone,
 * on any replica. One socket, and one message, for every kind of thing.
 *
 *   client: connect /events, then send {"type": "auth", "token": ...}
 *   server: {"type": "ready", ...}, then a change event for each change:
 *           {"type": "change", "kind": "comment", "action": "created",
 *            "id": ..., "actor": <Kinde id>, "scope": <e.g. the page>}
 *
 * A change says what happened, not what the row now holds: a client that
 * wants the row fetches it (e.g. GET /comments/{id}).
 *
 * Browsers can't set headers on a WebSocket, and a token in the URL would land
 * in access logs, so the token is the first message. The server closes:
 *
 *   4400  the first message wasn't an auth message
 *   4401  unauthenticated, or the token ran out (reconnect with a fresh one)
 *   1013  the client fell behind, or Kinde's keys are unavailable (retry soon)
 *   1012  this replica is shutting down (reconnect: another will answer)
 *
 * Only the messages' shapes reach the designs app through the spec; this path,
 * the handshake and these codes are mirrored by hand in its eventStream.ts. See
 * the README's "Keeping the backend and the designs app in step" before
 * changing any of them.
 *
 * OpenAPI spec version: 0.1.0
 */
import { designsBackendFetch } from './fetcher';
export type CommentResponseContent = { [key: string]: unknown };

export interface UserResponse {
  /** The Kinde user id. */
  id: string;
  email: string | null;
  givenName: string | null;
  familyName: string | null;
  fullName: string | null;
  /** A URL to their avatar. */
  picture: string | null;
}

export interface CommentResponse {
  id: string;
  /** Whoever wrote it. Details are null until they next sign in. */
  author: UserResponse;
  page: string;
  anchor: string;
  content: CommentResponseContent;
  createdAt: string;
  /** The comment this one replies to; null for a thread's first. */
  parentId: string | null;
}

/**
 * The comment itself; its shape is the client's to decide.
 */
export type CreateCommentRequestContent = { [key: string]: unknown };

export interface CreateCommentRequest {
  /**
   * The Loft page the comment is on, as its path.
   * @minLength 1
   * @maxLength 512
   */
  page: string;
  /**
   * The element on the page the comment is pinned to.
   * @minLength 1
   * @maxLength 512
   */
  anchor: string;
  /** The comment itself; its shape is the client's to decide. */
  content: CreateCommentRequestContent;
  /** The comment this one replies to, if any. */
  parentId?: string | null;
}

export interface ErrorResponse {
  detail: string;
}

export type ValidationErrorCtx = { [key: string]: unknown };

export interface ValidationError {
  loc: (string | number)[];
  msg: string;
  type: string;
  input?: unknown;
  ctx?: ValidationErrorCtx;
}

export interface HTTPValidationError {
  detail?: ValidationError[];
}

export interface ListCommentsResponse {
  comments: CommentResponse[];
}

export interface VersionResponse {
  /** The designs-backend package version. */
  version: string;
  /** The git commit the running image was built from. */
  commit: string;
  environment: string;
}

export type ChangeEventType =
  (typeof ChangeEventType)[keyof typeof ChangeEventType];

export const ChangeEventType = {
  change: 'change',
} as const;

export type ChangeEventAction =
  (typeof ChangeEventAction)[keyof typeof ChangeEventAction];

export const ChangeEventAction = {
  created: 'created',
  updated: 'updated',
  deleted: 'deleted',
} as const;

/**
 * What changed. A new kind is a member here and a trigger naming it.
 */
export type Kind = (typeof Kind)[keyof typeof Kind];

export const Kind = {
  comment: 'comment',
} as const;

export interface ChangeEvent {
  type: ChangeEventType;
  kind: Kind;
  action: ChangeEventAction;
  /** The changed row's id. */
  id: string;
  /** Kinde id of whoever made the change; null when no one did (a migration, a script). */
  actor: string | null;
  /** Where it belongs, for clients to filter on: a comment's page. */
  scope: string | null;
}

export type EventsReadyMessageType =
  (typeof EventsReadyMessageType)[keyof typeof EventsReadyMessageType];

export const EventsReadyMessageType = {
  ready: 'ready',
} as const;

/**
 * Server to client: authenticated; events follow.
 */
export interface EventsReadyMessage {
  type: EventsReadyMessageType;
  userId: string;
  /** When the token runs out; the server closes with 4401 then. */
  expiresAt: string | null;
}

export type EventsServerMessage = EventsReadyMessage | ChangeEvent;

/**
 * Client to server, first and only: who is connecting.
 */
export interface EventsAuthMessage {
  type: 'auth';
  /** A Kinde access token, as on HTTP requests. */
  token: string;
}

export type ListCommentsParams = {
  /**
   * Only this page's.
   */
  page?: string;
  /**
   * Only this anchor's.
   */
  anchor?: string;
  /**
   * @minimum 1
   * @maximum 500
   */
  limit?: number;
};

export const getGetVersionUrl = () => {
  return `/version`;
};

/**
 * What is deployed: the package version and the commit it was built from.
 *
 * Open, like the probes, so it can be checked without signing in.
 * @summary Get Version
 */
export const getVersion = async (
  options?: Parameters<typeof designsBackendFetch>[1],
): Promise<VersionResponse> => {
  return designsBackendFetch<VersionResponse>(getGetVersionUrl(), {
    ...options,
    method: 'GET',
  });
};

export const getListCommentsUrl = (params?: ListCommentsParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/comments?${stringifiedParams}`
    : `/comments`;
};

/**
 * Lists comments oldest first.
 * @summary List Comments
 */
export const listComments = async (
  params?: ListCommentsParams,
  options?: Parameters<typeof designsBackendFetch>[1],
): Promise<ListCommentsResponse> => {
  return designsBackendFetch<ListCommentsResponse>(getListCommentsUrl(params), {
    ...options,
    method: 'GET',
  });
};

export const getCreateCommentUrl = () => {
  return `/comments`;
};

/**
 * @summary Create Comment
 */
export const createComment = async (
  createCommentRequest: CreateCommentRequest,
  options?: Parameters<typeof designsBackendFetch>[1],
): Promise<CommentResponse> => {
  const getHeaders = (
    h?: NonNullable<RequestInit['headers']>,
  ): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(
          h as Iterable<Iterable<string>>,
          (entry) => Array.from(entry) as [string, string],
        ),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<
      string | readonly string[] | undefined
    >(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
  return designsBackendFetch<CommentResponse>(getCreateCommentUrl(), {
    ...options,
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getHeaders(options?.headers),
    },
    body: JSON.stringify(createCommentRequest),
  });
};

export const getGetCommentUrl = (commentId: string) => {
  return `/comments/${commentId}`;
};

/**
 * @summary Get Comment
 */
export const getComment = async (
  commentId: string,
  options?: Parameters<typeof designsBackendFetch>[1],
): Promise<CommentResponse> => {
  return designsBackendFetch<CommentResponse>(getGetCommentUrl(commentId), {
    ...options,
    method: 'GET',
  });
};

export const getDeleteCommentUrl = (commentId: string) => {
  return `/comments/${commentId}`;
};

/**
 * Deletes a comment and its replies; only its author may.
 * @summary Delete Comment
 */
export const deleteComment = async (
  commentId: string,
  options?: Parameters<typeof designsBackendFetch>[1],
): Promise<void> => {
  return designsBackendFetch<void>(getDeleteCommentUrl(commentId), {
    ...options,
    method: 'DELETE',
  });
};

export const getStartSessionUrl = () => {
  return `/session`;
};

/**
 * Checks the token, and stores the signed-in person's Kinde profile.
 *
 * The designs app calls this on page load. Answers 502 when Kinde won't
 * give the profile.
 * @summary Start Session
 */
export const startSession = async (
  options?: Parameters<typeof designsBackendFetch>[1],
): Promise<UserResponse> => {
  return designsBackendFetch<UserResponse>(getStartSessionUrl(), {
    ...options,
    method: 'POST',
  });
};
